import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, X, Loader2 } from "lucide-react";
import useFullscreen from "@/hooks/useFullscreen";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

const FlatVideoPlayer = ({ tour, tours = [], onClose, onSelectTour, soundOn = true, onToggleSound }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const { isFullscreen, toggleFullscreen } = useFullscreen(containerRef);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState({ cur: 0, dur: 0 });

  useEffect(() => {
    setLoading(true);
  }, [tour.id]);

  const onLoaded = () => {
    setLoading(false);
    const v = videoRef.current;
    if (v) {
      v.muted = true; // original audio silenced; ambient soundscape plays instead
      setTime({ cur: 0, dur: v.duration || 0 });
      v.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  };

  const onTime = () => {
    const v = videoRef.current;
    if (v && v.duration) {
      setProgress((v.currentTime / v.duration) * 100);
      setTime({ cur: v.currentTime, dur: v.duration });
    }
  };

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, []);

  const seek = (e) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    v.currentTime = pct * v.duration;
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !document.fullscreenElement) onClose();
      if (e.key === " ") {
        e.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, togglePlay]);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      data-testid="video-player"
    >
      <video
        ref={videoRef}
        className="max-h-full max-w-full h-full w-auto object-contain"
        playsInline
        muted
        loop
        onLoadedData={onLoaded}
        onTimeUpdate={onTime}
        onClick={togglePlay}
        data-testid="player-video"
      >
        <source src={tour.video_url} type="video/mp4" />
        {tour.video_url.endsWith(".mp4") && (
          <source src={tour.video_url.replace(/\.mp4$/, ".webm")} type="video/webm" />
        )}
      </video>

      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#04271B] z-20" data-testid="player-loading">
          <Loader2 className="w-8 h-8 text-[#D6B15E] animate-spin" strokeWidth={1.5} />
          <p className="mt-4 text-xs tracking-[0.2em] uppercase text-[#A9B8A6]">Loading tour</p>
        </div>
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between p-6">
        <div className="backdrop-blur-2xl bg-black/40 border border-white/10 px-5 py-3 rounded-full">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#D6B15E]">{tour.room}</p>
          <p className="font-heading text-lg leading-tight">{tour.title}</p>
        </div>
        <button
          onClick={onClose}
          data-testid="player-close-btn"
          className="w-12 h-12 flex items-center justify-center backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close player"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 z-30 p-6 flex flex-col items-center gap-4">
        {tours.length > 1 && (
          <div className="flex gap-3 max-w-full overflow-x-auto pb-1">
            {tours.map((t) => (
              <button
                key={t.id}
                onClick={() => onSelectTour(t)}
                data-testid={`player-scene-${t.id}`}
                className={`shrink-0 px-4 py-2 rounded-full text-xs tracking-widest uppercase border transition-colors backdrop-blur-2xl ${
                  t.id === tour.id ? "bg-[#D6B15E] text-black border-[#D6B15E]" : "bg-black/40 text-white border-white/10 hover:bg-white/10"
                }`}
              >
                {t.room}
              </button>
            ))}
          </div>
        )}

        <div className="w-full max-w-2xl backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full px-4 py-3 flex items-center gap-4 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
          <button
            onClick={togglePlay}
            data-testid="player-play-toggle"
            className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-black hover:bg-gray-200 transition-colors shrink-0"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause className="w-5 h-5" strokeWidth={1.5} /> : <Play className="w-5 h-5 ml-0.5" strokeWidth={1.5} />}
          </button>

          <span className="text-xs text-[#A9B8A6] tabular-nums w-10 text-center">{fmt(time.cur)}</span>
          <div className="flex-1 h-1.5 bg-white/15 rounded-full overflow-hidden cursor-pointer" onClick={seek} data-testid="player-seek">
            <div className="h-full bg-[#D6B15E]" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-xs text-[#A9B8A6] tabular-nums w-10 text-center">{fmt(time.dur)}</span>

          <button
            onClick={onToggleSound}
            data-testid="player-mute-toggle"
            title={soundOn ? "Mute ambient sound" : "Play ambient sound"}
            className="w-11 h-11 flex items-center justify-center rounded-full border border-white/10 hover:bg-white/10 transition-colors shrink-0"
            aria-label={soundOn ? "Mute ambient sound" : "Play ambient sound"}
          >
            {soundOn ? <Volume2 className="w-5 h-5 text-[#D6B15E]" strokeWidth={1.5} /> : <VolumeX className="w-5 h-5" strokeWidth={1.5} />}
          </button>
          <button
            onClick={toggleFullscreen}
            data-testid="player-fullscreen-toggle"
            className="w-11 h-11 flex items-center justify-center rounded-full border border-white/10 hover:bg-white/10 transition-colors shrink-0"
            aria-label="Toggle fullscreen"
          >
            {isFullscreen ? <Minimize className="w-5 h-5" strokeWidth={1.5} /> : <Maximize className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default FlatVideoPlayer;
